import { t } from "@lingui/core/macro";
import { type AnyObject, EMPTY_ARRAY, EMPTY_STRING, endOfDay, getKeys } from "@regrello/core-utils";
import type {
  SpectrumFieldConstraintFields,
  SpectrumFieldVersionFields,
  SpectrumValueConstraintFields,
} from "@regrello/graphql-api";
import { startOfDay } from "date-fns";
import type { FieldPath, FieldValues, PathValue, RegisterOptions, ValidationValueMessage } from "react-hook-form";

import {
  AllAllowedFileTypes,
  type FrontendAllowedFileTypes,
  ValidationRules,
} from "../../../../constants/globalConstants";
import { isFrontendFieldValueEmpty } from "../../../../utils/actionItemUtils";

export enum FrontendValueConstraintRuleName {
  DECIMAL_PLACES = "decimal_places",
  DISALLOW_GROUPINGS_FLOAT = "disallow_groupings_float",

  MAX_CHARACTER = "max_characters",
  MIN_CHARACTER = "min_characters",

  MAX_INT = "max_int",
  MIN_INT = "min_int",

  MAX_FLOAT = "max_float",
  MIN_FLOAT = "min_float",

  MAX_PARTY = "max_parties",

  BEFORE_OR_AFTER_TODAY_INCLUSIVE = "before_or_after_today_inclusive",

  EMAIL = "email",

  DOCUMENT_TYPE = "document_type",
  DOCUMENT_DISALLOW_LINK = "document_disallow_link",
}

export const ON_OR_AFTER_TODAY_ARG = "1";
export const ON_OR_BEFORE_TODAY_ARG = "-1";

/** Max party is -1, BE will ignore this constraint. */
export const ALLOW_MULTIPLE_PARTY_ARG = "-1";

/** Max party is 1. */
export const DISALLOW_MULTIPLE_PARTY_ARG = "1";

/**
 * Returns the number of decimal places configured in the provided field version (assuming it's
 * stored as the field constraint).
 *
 * @param fieldVersion - The field version to get the decimal places from.
 * @returns The number of decimal places, or undefined if the decimal places constraint is not found
 */
export function getDecimalPlacesFromFieldVersion(
  fieldVersion: Pick<SpectrumFieldVersionFields, "fieldConstraints">,
): number | undefined {
  const constraints = fieldVersion.fieldConstraints;

  if (constraints == null || constraints.length === 0) {
    return undefined;
  }

  const decimalPlacesConstraint = constraints.find(
    (fieldConstraint) =>
      fieldConstraint.spectrumValueConstraint.valueConstraintRule === FrontendValueConstraintRuleName.DECIMAL_PLACES,
  );

  if (decimalPlacesConstraint == null) {
    return undefined;
  }

  // Extract the decimal places value from the constraint.
  const decimalPlaces = Number.parseInt(decimalPlacesConstraint.constraintArgs[0], 10);
  if (Number.isNaN(decimalPlaces) || decimalPlaces < 0) {
    return undefined;
  }

  return decimalPlaces;
}

/**
 * Returns whether the "disallow_groupings_float" flag is true or false in the provided field
 * version. If true, the value should appear with thousands separators. If false, thousands
 * separators should not be shown.
 *
 * @param fieldVersion - The field version to get the disallow_groupings_float from.
 * @returns The disallow_groupings_float value, or undefined if it is not configured.
 */
export function getDisallowGroupingsFloatFromFieldVersion(
  fieldVersion: Pick<SpectrumFieldVersionFields, "fieldConstraints">,
): boolean | undefined {
  const constraints = fieldVersion.fieldConstraints;

  if (constraints == null || constraints.length === 0) {
    return undefined;
  }

  const targetConstraint = constraints.find(
    (fieldConstraint) =>
      fieldConstraint.spectrumValueConstraint.valueConstraintRule ===
      FrontendValueConstraintRuleName.DISALLOW_GROUPINGS_FLOAT,
  );

  if (targetConstraint == null) {
    return undefined;
  }

  const argValue = targetConstraint.constraintArgs[0];
  if (argValue === "true") {
    return true;
  }
  if (argValue === "false") {
    return false;
  }
  return undefined;
}

export function getValidationRuleFromFieldConstraint(fieldConstraint: SpectrumFieldConstraintFields) {
  const constraintArgs = fieldConstraint.constraintArgs;
  switch (fieldConstraint.spectrumValueConstraint.valueConstraintRule) {
    case FrontendValueConstraintRuleName.DECIMAL_PLACES:
      // The number of decimal places will be enforced on the frontend by the RegrelloNumericInput
      // component (or a wrapper of it), not by a form-level validation rule.
      return undefined;
    case FrontendValueConstraintRuleName.DISALLOW_GROUPINGS_FLOAT:
      // This is strictly a presentational constraint whose format will be automatically applied by
      // the RegrelloNumericInput component, not by a form-level validation rule. It is only
      // relevant in Works Thru Email, Bulk Start Workflows, and similar use cases.
      return undefined;
    case FrontendValueConstraintRuleName.MIN_CHARACTER:
      return ValidationRules.MIN_LENGTH(Number.parseInt(constraintArgs[0], 10));
    case FrontendValueConstraintRuleName.MAX_CHARACTER:
      return ValidationRules.MAX_LENGTH(Number.parseInt(constraintArgs[0], 10));
    case FrontendValueConstraintRuleName.MIN_INT:
      return ValidationRules.GREATER_THAN_OR_EQUAL_TO_LOWER_BOUND(Number.parseInt(constraintArgs[0], 10));
    case FrontendValueConstraintRuleName.MIN_FLOAT:
      return ValidationRules.GREATER_THAN_OR_EQUAL_TO_LOWER_BOUND(Number.parseFloat(constraintArgs[0]));
    case FrontendValueConstraintRuleName.MAX_INT:
      return ValidationRules.LESS_THAN_OR_EQUAL_TO_UPPER_BOUND(Number.parseInt(constraintArgs[0], 10));
    case FrontendValueConstraintRuleName.MAX_FLOAT:
      return ValidationRules.LESS_THAN_OR_EQUAL_TO_UPPER_BOUND(Number.parseFloat(constraintArgs[0]));
    case FrontendValueConstraintRuleName.EMAIL:
      return ValidationRules.VALID_EMAIL;
    case FrontendValueConstraintRuleName.BEFORE_OR_AFTER_TODAY_INCLUSIVE: {
      if (constraintArgs.length === 0) {
        return undefined;
      }
      if (Number.parseInt(constraintArgs[0], 10) > 0) {
        return ValidationRules.AFTER_DATE(startOfDay(new Date()));
      }
      return ValidationRules.BEFORE_DATE(endOfDay(new Date()));
    }
    case FrontendValueConstraintRuleName.DOCUMENT_TYPE:
      return ValidationRules.VALID_DOCUMENT_FORMAT(
        constraintArgs.filter((arg) =>
          AllAllowedFileTypes.includes(arg as FrontendAllowedFileTypes),
        ) as FrontendAllowedFileTypes[],
      );
    default:
      return undefined;
  }
}

export function getValueConstraintDisplayName(valueConstraint: SpectrumValueConstraintFields) {
  switch (valueConstraint.valueConstraintRule) {
    // (clewis): Keep cases alphabetized.
    case FrontendValueConstraintRuleName.BEFORE_OR_AFTER_TODAY_INCLUSIVE:
      return t`Accepted dates`;
    case FrontendValueConstraintRuleName.DECIMAL_PLACES:
      return t`Decimal places`;
    case FrontendValueConstraintRuleName.DISALLOW_GROUPINGS_FLOAT:
      return t`Separators`;
    case FrontendValueConstraintRuleName.DOCUMENT_DISALLOW_LINK:
      return t`Disallow external link`;
    case FrontendValueConstraintRuleName.DOCUMENT_TYPE:
      return t`File extensions`;
    case FrontendValueConstraintRuleName.MAX_CHARACTER:
      return t`Maximum length`;
    case FrontendValueConstraintRuleName.MAX_FLOAT:
      return t`Maximum value`;
    case FrontendValueConstraintRuleName.MAX_INT:
      return t`Maximum value`;
    case FrontendValueConstraintRuleName.MAX_PARTY:
      return t`Allow adding multiple people or teams`;
    case FrontendValueConstraintRuleName.MIN_CHARACTER:
      return t`Minimum length`;
    case FrontendValueConstraintRuleName.MIN_FLOAT:
      return t`Minimum value`;
    case FrontendValueConstraintRuleName.MIN_INT:
      return t`Minimum value`;
    default:
      return EMPTY_STRING;
  }
}

export function getValueConstraintDisplayValue(fieldConstraint: SpectrumFieldConstraintFields) {
  const joinWithCommas = (args: string[]) => args.join(", ");

  switch (fieldConstraint.spectrumValueConstraint.valueConstraintRule) {
    // (clewis): Keep cases alphabetized.
    case FrontendValueConstraintRuleName.BEFORE_OR_AFTER_TODAY_INCLUSIVE:
      return joinWithCommas(fieldConstraint.constraintArgs);
    case FrontendValueConstraintRuleName.DECIMAL_PLACES:
      return joinWithCommas(fieldConstraint.constraintArgs);
    case FrontendValueConstraintRuleName.DISALLOW_GROUPINGS_FLOAT:
      return fieldConstraint.constraintArgs[0] === "true" ? t`Disabled` : t`Enabled`;
    case FrontendValueConstraintRuleName.DOCUMENT_DISALLOW_LINK:
      return joinWithCommas(fieldConstraint.constraintArgs);
    case FrontendValueConstraintRuleName.DOCUMENT_TYPE:
      return joinWithCommas(fieldConstraint.constraintArgs);
    case FrontendValueConstraintRuleName.MAX_CHARACTER:
      return joinWithCommas(fieldConstraint.constraintArgs);
    case FrontendValueConstraintRuleName.MAX_FLOAT:
      return joinWithCommas(fieldConstraint.constraintArgs);
    case FrontendValueConstraintRuleName.MAX_INT:
      return joinWithCommas(fieldConstraint.constraintArgs);
    case FrontendValueConstraintRuleName.MAX_PARTY:
      return joinWithCommas(fieldConstraint.constraintArgs);
    case FrontendValueConstraintRuleName.MIN_CHARACTER:
      return joinWithCommas(fieldConstraint.constraintArgs);
    case FrontendValueConstraintRuleName.MIN_FLOAT:
      return joinWithCommas(fieldConstraint.constraintArgs);
    case FrontendValueConstraintRuleName.MIN_INT:
      return joinWithCommas(fieldConstraint.constraintArgs);
    default:
      return EMPTY_STRING;
  }
}

export function getIsValueConstraintEmpty(valueConstraint: SpectrumValueConstraintFields, args: string[]) {
  if (args.length === 0) {
    return true;
  }

  switch (valueConstraint.valueConstraintRule) {
    case FrontendValueConstraintRuleName.DOCUMENT_DISALLOW_LINK:
      return args[0] === "false";
    case FrontendValueConstraintRuleName.BEFORE_OR_AFTER_TODAY_INCLUSIVE:
      return args[0] === "0";
    case FrontendValueConstraintRuleName.MAX_PARTY:
      return args[0] === "1";
    default:
      return args.every((arg) => arg.length === 0);
  }
}

export function getNumberOfArgsByConstraint(valueConstraint: SpectrumValueConstraintFields) {
  switch (valueConstraint.valueConstraintRule) {
    case FrontendValueConstraintRuleName.DECIMAL_PLACES:
    case FrontendValueConstraintRuleName.DISALLOW_GROUPINGS_FLOAT:
    case FrontendValueConstraintRuleName.MIN_CHARACTER:
    case FrontendValueConstraintRuleName.MAX_CHARACTER:
    case FrontendValueConstraintRuleName.MIN_INT:
    case FrontendValueConstraintRuleName.MAX_INT:
    case FrontendValueConstraintRuleName.MIN_FLOAT:
    case FrontendValueConstraintRuleName.MAX_FLOAT:
    case FrontendValueConstraintRuleName.MAX_PARTY:
    case FrontendValueConstraintRuleName.BEFORE_OR_AFTER_TODAY_INCLUSIVE:
    case FrontendValueConstraintRuleName.DOCUMENT_DISALLOW_LINK:
      return 1;
    case FrontendValueConstraintRuleName.EMAIL:
      return 0;
    default:
      return 0;
  }
}

export type ValidationRule<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = Omit<
  RegisterOptions<TFieldValues, TName>,
  "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
>;

export function getAllValidationRulesFromFieldConstraints<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(isRequired: boolean, fieldConstraints: SpectrumFieldConstraintFields[]): ValidationRule<TFieldValues, TName> {
  const result = fieldConstraints.reduce<AnyObject>((validationRules, fieldConstraint) => {
    const validationRule = getValidationRuleFromFieldConstraint(fieldConstraint);
    if (validationRule == null) {
      return validationRules;
    }
    // (hchen): We have validated the rule is defined, it must have a key
    const ruleName = getKeys(validationRule)[0];
    validationRules[ruleName] = validationRule[ruleName];
    return validationRules;
  }, {});
  if (isRequired) {
    return {
      ...ValidationRules.REQUIRED,
      ...result,
    };
  }
  return {
    ...ValidationRules.NOT_REQUIRED,
    ...result,
  };
}

/**
 * Evaluates a validation rule and returns isValid and errorMessages. Normally this is done by
 * react-hook-form, but we need to do it manually here as we are using the same rules for
 * RegrelloDataGridV2 without the form. Only one error message is returned, as only one rule should
 * take precedence.
 *
 * @param rules - The validation rules to evaluate.
 * @param value - The value to evaluate the validation rules on.
 * @returns The `isValid` and `errorMessage`.
 */
export function evaluateValidationRule<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
  rules: ValidationRule<TFieldValues, TName>,
  value: PathValue<TFieldValues, TName>,
) {
  let isValid = true;
  let errorMessage = EMPTY_STRING;
  const { validate, required, min, max, minLength, maxLength } = rules;
  if (value != null && validate != null && typeof validate === "function") {
    const result = validate(value, {} as TFieldValues);
    if (typeof result === "string") {
      errorMessage = result;
      isValid = false;
    }
  }
  if (required != null) {
    if (typeof required === "boolean") {
      if (required && isFrontendFieldValueEmpty(value)) {
        errorMessage = t`This field is required`;
        isValid = false;
      }
    } else if (typeof required === "string") {
      if (required && isFrontendFieldValueEmpty(value)) {
        errorMessage = required;
        isValid = false;
      }
    } else if (required.value && isFrontendFieldValueEmpty(value)) {
      errorMessage = required.message;
      isValid = false;
    }
  }
  if (min != null) {
    if (typeof min === "number" && typeof value === "number") {
      if (value < min) {
        errorMessage = t`Must be greater than ${min}`;
        isValid = false;
      }
    } else if (typeof value === "number") {
      const minRuleWithMessage = min as ValidationValueMessage<number>;
      if (value < minRuleWithMessage.value) {
        errorMessage = minRuleWithMessage.message;
        isValid = false;
      }
    }
  }
  if (max != null) {
    if (typeof max === "number" && typeof value === "number") {
      if (value > max) {
        errorMessage = t`Must be less than ${max}`;
        isValid = false;
      }
    } else if (typeof value === "number") {
      const maxRuleWithMessage = max as ValidationValueMessage<number>;
      if (value > maxRuleWithMessage.value) {
        errorMessage = maxRuleWithMessage.message;
        isValid = false;
      }
    }
  }
  if (minLength != null) {
    if (typeof minLength === "number" && typeof value === "string") {
      if (value.length < minLength) {
        errorMessage = t`Must have at least ${minLength} characters`;
        isValid = false;
      }
    } else if (typeof value === "string") {
      const minLengthRuleWithMessage = minLength as ValidationValueMessage<number>;
      if (value.length < minLengthRuleWithMessage.value) {
        errorMessage = minLengthRuleWithMessage.message;
        isValid = false;
      }
    }
  }
  if (maxLength != null) {
    if (typeof maxLength === "number" && typeof value === "string") {
      if (value.length > maxLength) {
        errorMessage = t`Must have at most ${maxLength} characters`;
        isValid = false;
      }
    } else if (typeof value === "string") {
      const maxLengthRuleWithMessage = maxLength as ValidationValueMessage<number>;
      if (value.length > maxLengthRuleWithMessage.value) {
        errorMessage = maxLengthRuleWithMessage.message;
        isValid = false;
      }
    }
  }
  return { isValid, errorMessage };
}

export interface FrontendActiveRules {
  show: boolean;
  hide: boolean;
  required: boolean;
}

export function getActiveRuleAsBoolean(
  spectrumRuleMetadata: Array<{ type: string; value: string }> | undefined,
): FrontendActiveRules {
  const rules: FrontendActiveRules = {
    show: false,
    hide: false,
    required: false,
  };
  if (spectrumRuleMetadata == null) {
    return rules;
  }
  for (const { value } of spectrumRuleMetadata) {
    switch (value) {
      case "hide":
        rules.hide = true;
        break;
      case "show":
        rules.show = true;
        break;
      case "mandatory":
        rules.required = true;
        rules.show = true;
        break;
      default:
        break;
    }
  }
  return rules;
}

export function getAllowedFileTypes(spectrumFieldVersion: SpectrumFieldVersionFields) {
  for (const constraint of spectrumFieldVersion.fieldConstraints) {
    if (constraint.spectrumValueConstraint.valueConstraintRule === FrontendValueConstraintRuleName.DOCUMENT_TYPE) {
      return constraint.constraintArgs.map((fileExtension) => `.${fileExtension}` as `.${string}`);
    }
  }
  return EMPTY_ARRAY;
}

export function getIsLinkDisallowed(spectrumFieldVersion: SpectrumFieldVersionFields) {
  for (const constraint of spectrumFieldVersion.fieldConstraints) {
    if (
      constraint.spectrumValueConstraint.valueConstraintRule === FrontendValueConstraintRuleName.DOCUMENT_DISALLOW_LINK
    ) {
      return constraint.constraintArgs.length > 0 && constraint.constraintArgs[0] === "true";
    }
  }
  return false;
}

export function getIsMultiPartyAllowed(spectrumFieldVersion: SpectrumFieldVersionFields) {
  for (const constraint of spectrumFieldVersion.fieldConstraints) {
    if (constraint.spectrumValueConstraint.valueConstraintRule === FrontendValueConstraintRuleName.MAX_PARTY) {
      return constraint.constraintArgs.length > 0 && constraint.constraintArgs[0] === ALLOW_MULTIPLE_PARTY_ARG;
    }
  }
  return false;
}

export function getMinValueFromConstraint(spectrumFieldVersion: SpectrumFieldVersionFields): number | undefined {
  for (const constraint of spectrumFieldVersion.fieldConstraints) {
    if (constraint.spectrumValueConstraint.valueConstraintRule === FrontendValueConstraintRuleName.MIN_INT) {
      return Number.parseInt(constraint.constraintArgs[0], 10);
    }
    if (constraint.spectrumValueConstraint.valueConstraintRule === FrontendValueConstraintRuleName.MIN_FLOAT) {
      return Number.parseFloat(constraint.constraintArgs[0]);
    }
  }
  return undefined;
}

export function getMaxValueFromConstraint(spectrumFieldVersion: SpectrumFieldVersionFields): number | undefined {
  for (const constraint of spectrumFieldVersion.fieldConstraints) {
    if (constraint.spectrumValueConstraint.valueConstraintRule === FrontendValueConstraintRuleName.MAX_INT) {
      return Number.parseInt(constraint.constraintArgs[0], 10);
    }
    if (constraint.spectrumValueConstraint.valueConstraintRule === FrontendValueConstraintRuleName.MAX_FLOAT) {
      return Number.parseFloat(constraint.constraintArgs[0]);
    }
  }
  return undefined;
}
